import { useCallback } from "react"
import type { CombinedError } from "urql"
import { useMutation } from "urql"

import type {
  CommentCreateInput,
  CreateCommentMutationMutation,
  CreateCommentMutationMutationVariables,
  GoodsReceipt,
  GoodsReceiptMutationProcessReceiptArgs,
  GoodsReceiptUpdateFailure,
  ProcessGoodsReceiptMutation,
} from "@/graphql/codegen/graphql"
import {
  CommentParentTableType,
  CreateCommentMutationDocument,
  GoodsReceiptStatus,
  ProcessGoodsReceiptDocument,
} from "@/graphql/codegen/graphql"
import { useAppView } from "@/hooks/useAppView"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export interface SoftReceiveInput {
  estimatedPalletsCount?: number | null
  deliveredPalletsCount: number | null
  spacesUsed?: number | null
  comment?: string | null
}

interface UseSoftReceiveGoodsReceiptReturn {
  softReceive: (
    goodsReceipt: GoodsReceipt,
    input: SoftReceiveInput,
    onSuccess: () => void
  ) => Promise<GoodsReceipt | null>
  isReceiving: boolean
  error?: CombinedError
}

export const useSoftReceiveGoodsReceipt = (): UseSoftReceiveGoodsReceiptReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { isTabletView } = useAppView()

  const [{ fetching: isSoftReceiving, error }, executeSoftReceive] = useMutation<
    ProcessGoodsReceiptMutation,
    GoodsReceiptMutationProcessReceiptArgs
  >(ProcessGoodsReceiptDocument)

  const [{ fetching: isCreatingComment }, executeCreateComment] = useMutation<
    CreateCommentMutationMutation,
    CreateCommentMutationMutationVariables
  >(CreateCommentMutationDocument)

  const softReceive = useCallback(
    async (goodsReceipt: GoodsReceipt, input: SoftReceiveInput, onSuccess: () => void) => {
      const { comment, ...softReceiveData } = input
      // First, process the receipt to SOFT_RECEIVED status
      const { data, error } = await executeSoftReceive({
        input: {
          goodsReceiptId: goodsReceipt.goodsReceiptId,
          status: GoodsReceiptStatus.SoftReceived,
          softReceiveData,
        },
      })

      const output = data?.goodsReceipt.processReceipt
      const hasError = error || output?.__typename === "GoodsReceiptUpdateFailure"

      if (hasError) {
        const errorMessage =
          error?.message ?? (output as GoodsReceiptUpdateFailure).error ?? "Failed to soft receive goods receipt."
        notify({
          type: NotificationType.Error,
          message: errorMessage,
        })
        throw new Error(errorMessage)
      }

      // If comment is provided, create it as a separate operation
      if (comment?.trim()) {
        const commentInput: CommentCreateInput = {
          value: comment,
          referenceId: goodsReceipt.goodsReceiptId,
          type: CommentParentTableType.GoodsReceipt,
          isActive: true,
        }

        const { data: _commentData, error: commentError } = await executeCreateComment({ input: commentInput })

        if (commentError) {
          notify({
            type: NotificationType.Error,
            message: commentError.message,
          })

          throw new Error(commentError?.message ?? "Failed to create comment.")
        }
      }

      if (!isTabletView) {
        notify({
          type: NotificationType.Success,
          message: `Goods Receipt #${goodsReceipt.agrNumber} has been successfully Soft Received`,
        })
      }

      onSuccess()

      return output?.goodsReceipt as GoodsReceipt | null
    },
    [executeSoftReceive, executeCreateComment, notify, isTabletView]
  )

  return {
    softReceive,
    isReceiving: isSoftReceiving || isCreatingComment,
    error,
  }
}
